import type { SearchParamsType } from '$lib/services/urls/searchParams';
import type { Page } from '@sveltejs/kit';
import type { BoundaryCoordinates } from '../map/types';
import { ROUTE_GROUP_ON_THE_WAY_POI, ROUTE_GROUP_SEARCH_POI, ROUTE_GROUP_ZOI_POI } from '$lib/constants/route';
import { isAnyPoiPartner } from '$lib/constants/sponsoredPoi';
import { getPageNameWithCategory } from '$lib/services/urls/routeIds';
import { type Pagename, routesPagename } from '$lib/services/urls/routesPagename';
import { transformStringDateToCalendarDate } from '$lib/utils/date';
import { formatPrice } from '$lib/utils/formatPrice';
import { type BookingParams, getBookingUrl } from '$lib/utils/getBookingUrl';
import { languageTag } from '$paraglide/runtime';
import {
	type AccommodationMichelinPoiData,
	type AccommodationPoiData,
	type DatasheetPoiData,
	type DatasheetPoiType,
	datasheetPoiTypeList,
	type DatasheetSideBarPoiData,
	type DatasheetSideBarPoiType,
	datasheetSideBarPoiTypeList,
	type DateRange,
	type MainPoiCategory,
	mainPoiCategoryList,
	type PoiCategory,
	type PoiData,
	type RestaurantMichelinPoiData,
	type RestaurantPoiData,
	type SummaryPoiData,
	type SummaryPoiType,
	summaryPoiTypeList,
	type TourismPoiData,
} from './types';

export function isAccommodationPoi(poi?: PoiData): poi is AccommodationPoiData {
	if (!poi) {
		return false;
	}
	return poi.__typename === 'AccommodationPOI';
}

export function isAccommodationMichelinPoi(poi?: PoiData): poi is AccommodationMichelinPoiData {
	if (!poi) {
		return false;
	}
	return poi.__typename === 'AccommodationMichelinPOI';
}

export function isRestaurantPoi(poi?: PoiData): poi is RestaurantPoiData {
	if (!poi) {
		return false;
	}
	return poi.__typename === 'RestaurantPOI';
}

export function isRestaurantMichelinPoi(poi?: PoiData): poi is RestaurantMichelinPoiData {
	if (!poi) {
		return false;
	}
	return poi.__typename === 'RestaurantMichelinPOI';
}

export function isTourismPoi(poi?: PoiData): poi is TourismPoiData {
	if (!poi) {
		return false;
	}
	return poi.__typename === 'TourismPOI';
}

export function isHRTPoi(poi?: PoiData): boolean {
	if (!poi) {
		return false;
	}
	return isAnyAccommodationPoi(poi) || isRestaurantMichelinPoi(poi) || isRestaurantPoi(poi) || isTourismPoi(poi);
}

export function isAnyAccommodationPoi(
	poi: PoiData,
): poi is AccommodationMichelinPoiData | AccommodationPoiData {
	return isAccommodationPoi(poi) || isAccommodationMichelinPoi(poi);
}

/** This function returns true if a poi is a "side bar" poi, i.e. a poi where a datasheet should be displayed on the side bar */
export function isDatasheetSideBarPoiData(poi?: PoiData): poi is DatasheetSideBarPoiData {
	if (!poi) {
		return false;
	}
	return datasheetSideBarPoiTypeList.includes(
		(poi as { __typename: DatasheetSideBarPoiType }).__typename,
	);
}

export function isDatasheetPoiData(poi?: PoiData): poi is DatasheetPoiData {
	if (!poi) {
		return false;
	}
	return datasheetPoiTypeList.includes((poi as { __typename: DatasheetPoiType }).__typename);
}

export function isSummaryPoiData(poi?: PoiData): poi is SummaryPoiData {
	if (!poi) {
		return false;
	}
	return summaryPoiTypeList.includes((poi as { __typename: SummaryPoiType }).__typename);
}

export function mapPoiToCategory(poi: PoiData): PoiCategory {
	switch (poi.__typename) {
		case 'AccommodationPOI':
		case 'AccommodationMichelinPOI':
			return 'ACCOMMODATION';
		case 'RestaurantPOI':
		case 'RestaurantMichelinPOI':
			return 'RESTAURANT';
		case 'TourismPOI':
			return 'TOURISM';
		case 'ParkingPOI':
			return 'PARKING';
		case 'ServiceGasStationPOI':
			return 'SERVICE_GAS_STATION';
		case 'ServiceEVPOI':
			return 'EV_SERVICE';
		case 'ServiceRestStopPOI':
			return 'REST_STOP';
		// To be modified with the new "service" poi category
		case 'ServicePOI':
			return 'EV_SERVICE';
		case 'PartnerPOI':
			return 'PARTNER';
		case 'Partner2POI':
			return 'PARTNER2';
	}
}

/** This function returns bounds (quite) centered on a poi */
export function getBoundsFromPoi(poi: PoiData): BoundaryCoordinates {
	// Due to Mercator projection, the size of a degree of longitude depends on the latitude
	// However the difference is small enough to be ignored here
	// So, we use a constant offset of 0.05 degrees that will be added or substracted to the center of the poi to get the bounds NE and SW corners
	const OFFSET = 0.05;
	return [
		[poi.coordinates.lng - OFFSET, poi.coordinates.lat - OFFSET],
		[poi.coordinates.lng + OFFSET, poi.coordinates.lat + OFFSET],
	];
}

export function isMainPoiCategory(poi: PoiCategory): poi is MainPoiCategory {
	return mainPoiCategoryList.includes(poi as MainPoiCategory);
}

export function getBookUrl(
	poi: DatasheetPoiData,
	page: Page,
	searchParams?: SearchParamsType,
) {
	const poiType = poi.__typename;

	if (poiType === 'AccommodationPOI') {
		const { bookUrl, id: poiId, srcPoiId, startingPrice, subType, stars } = poi;
		const departure = searchParams?.itinerary?.at(0);
		const arrival = searchParams?.itinerary?.at(-1);
		const { startDate, endDate } = searchParams?.filters?.ACCOMMODATION_AVAILABILITY_DATE_RANGE as DateRange || {};

		const checkin = transformStringDateToCalendarDate(startDate);
		const checkout = transformStringDateToCalendarDate(endDate);

		const priceMinAccommodationWithCurrency = startingPrice
			? formatPrice({
					amount: startingPrice.amount,
					currency: startingPrice.currency,
					language: languageTag(),
				})
			: '';

		const { pagename } = page.data;
		const mapPagenameFromRouteForBooking: Record<string, Pagename> = {
			[ROUTE_GROUP_SEARCH_POI]: routesPagename.ROUTE_EXPLORATION_FREE,
			[ROUTE_GROUP_ZOI_POI]: routesPagename.ROUTE_EXPLORATION_ZOI,
			[ROUTE_GROUP_ON_THE_WAY_POI]: routesPagename.ROUTE_ON_THE_WAY,
			'/(map)/map/[[category=poiCategory]]/poi/[...poi]': searchParams?.selectedAddress?.address ? routesPagename.MAP_RESULT : routesPagename.HOME_PAGE,
		};

		const pageName = page.route?.id && page.route.id in mapPagenameFromRouteForBooking ? mapPagenameFromRouteForBooking?.[page.route.id] : pagename;

		const options: BookingParams = {
			bookUrl,
			subType,
			poiId,
			contentType: pagename?.toLowerCase().includes('datasheet') ? 'datasheet' : 'poilist',
			priceMinAccommodationWithCurrency,
			srcPoiId: srcPoiId ?? '',
			pageName,
			star: stars || undefined,
			arrival: arrival?.label || searchParams?.selectedAddress?.address || '',
			departure: departure?.label || '',
			distance: '',
			checkin,
			checkout,
		};

		return getBookingUrl(options);
	}

	if (poiType === 'AccommodationMichelinPOI') {
		const url = decodeURIComponent(poi.bookUrl);
		const pageName = getPageNameWithCategory(page, searchParams);

		return pageName ? url.replace('{{pageName}}', pageName) : url;
	}

	if (isAnyPoiPartner(poi)) {
		return poi.bookUrl;
	}

	return undefined;
}
